/**
 * Regeln fuer die Zusammenstellung einer Angriffswelle
 * (Masterprompt Abschnitte 64, 68, 70).
 */

import { ARMY_POINT_CAP, MAX_BOSSES_PER_WAVE, type CreepTier } from '@dueline/game-data';
import { requireCreep } from './economy.js';

/** Die drei Reihen des Army Composers. */
export type WaveRow = 'front' | 'center' | 'back';

/** Eine geplante Welle: pro Reihe eine Liste von Creep-IDs. */
export interface WavePlan {
  front: readonly string[];
  center: readonly string[];
  back: readonly string[];
}

/** Eine leere Welle. */
export function emptyWavePlan(): WavePlan {
  return { front: [], center: [], back: [] };
}

/** Alle Creeps einer Welle in Marschreihenfolge: erst Front, dann Center, dann Back. */
export function flattenWave(plan: WavePlan): string[] {
  return [...plan.front, ...plan.center, ...plan.back];
}

/** Summe der Army Points einer Welle. */
export function calculateArmyPoints(plan: WavePlan): number {
  return flattenWave(plan).reduce((sum, id) => sum + requireCreep(id).armyPoints, 0);
}

/** Maximal erlaubte Army Points beim aktuellen Tier. */
export function getArmyPointCap(tier: CreepTier): number {
  return ARMY_POINT_CAP[tier];
}

/** Anzahl der Boss-Creeps in einer Welle. */
export function countBosses(plan: WavePlan): number {
  return flattenWave(plan).filter((id) => requireCreep(id).tags.includes('boss')).length;
}

/** Gruende, aus denen eine Welle ungueltig sein kann. */
export type WaveValidationError =
  'empty_wave' | 'army_cap_exceeded' | 'too_many_bosses' | 'creep_tier_locked' | 'unknown_creep';

export interface WaveValidationResult {
  valid: boolean;
  errors: WaveValidationError[];
  armyPoints: number;
  armyPointCap: number;
}

/**
 * Prueft eine Welle vollstaendig gegen die Regeln.
 * Der Server muss diese Pruefung bei jedem SEND ausfuehren -
 * der Client darf niemals die letzte Instanz sein.
 */
export function validateWave(plan: WavePlan, tier: CreepTier): WaveValidationResult {
  const errors: WaveValidationError[] = [];
  const ids = flattenWave(plan);
  const cap = getArmyPointCap(tier);

  if (ids.length === 0) {
    errors.push('empty_wave');
    return { valid: false, errors, armyPoints: 0, armyPointCap: cap };
  }

  let armyPoints = 0;
  let bosses = 0;
  let tierLocked = false;
  let unknown = false;

  for (const id of ids) {
    try {
      const creep = requireCreep(id);
      armyPoints += creep.armyPoints;
      if (creep.tags.includes('boss')) bosses += 1;
      if (creep.tier > tier) tierLocked = true;
    } catch {
      unknown = true;
    }
  }

  if (unknown) errors.push('unknown_creep');
  if (tierLocked) errors.push('creep_tier_locked');
  if (armyPoints > cap) errors.push('army_cap_exceeded');
  if (bosses > MAX_BOSSES_PER_WAVE) errors.push('too_many_bosses');

  return { valid: errors.length === 0, errors, armyPoints, armyPointCap: cap };
}
